import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Heading,
  Text,
  Link,
  Select,
  useToast,
  Flex,
  InputGroup,
  InputRightElement,
  DarkMode,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import bmLogo from "../assets/svg/bm_logo.svg";
import signInImage from "../assets/img/signInImage.png";
import Footer from "../components/Footer/Footer";
import GradientBorder from "../components/GradientBorder/GradientBorder";
import i18n from "../i18n";

const Register = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [howDidYouHear, setHowDidYouHear] = useState("");
  const [otherSource, setOtherSource] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Logic for language detection from Login.tsx
  const [isLanguageSet, setIsLanguageSet] = useState(false);
  const location = useLocation();

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { signupWithEmail } = useAuth();
  const toast = useToast();

  const titleColor = "white";
  const textColor = "gray.400";

  useEffect(() => {
    if (isLanguageSet) return;

    const params = new URLSearchParams(location.search);
    const urlLanguage = params.get("lang");
    const userLanguage = navigator.language;
    const browserLanguageCode = userLanguage.split("-")[0];
    const languageCode = urlLanguage || browserLanguageCode;

    if (["fr", "en", "es", "it", "de", "jp"].includes(String(languageCode).toLowerCase())) {
      i18n
        .changeLanguage(String(languageCode).toLowerCase())
        .then(() => {
          setIsLanguageSet(true);
        })
        .catch((err) => {
          console.error("Erreur lors du changement de langue:", err);
        });
    } else {
      setIsLanguageSet(true);
    }
  }, [location, isLanguageSet]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast({
        title: t("passwordMismatch"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    try {
      await signupWithEmail(email, password, companyName, howDidYouHear === "other" ? otherSource : howDidYouHear);
      toast({
        title: t("registrationSuccess"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      navigate("/dashboard");
    } catch (error) {
      toast({
        title: t("registrationError"),
        description: (error as Error).message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Flex position="relative" h="100vh" overflow="hidden">
      <Flex
        h="100%"
        w="100%"
        maxW="1044px"
        mx="auto"
        pt={{ sm: "100px", md: "0px" }}
        flexDirection="column"
        me={{ base: "auto", lg: "50px", xl: "auto" }}
      >
        <Flex
          alignItems="center"
          justifyContent="start"
          style={{ userSelect: "none" }}
          mx={{ base: "auto", lg: "unset" }}
          ms={{ base: "auto", lg: "auto" }}
          w={{ base: "100%", md: "50%", lg: "450px" }}
          px="50px"
          flexDirection="column"
          h="100%"
          overflowY="auto"
          css={{
            "&::-webkit-scrollbar": {
              width: "4px",
            },
            "&::-webkit-scrollbar-track": {
              width: "6px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "gray",
              borderRadius: "24px",
            },
          }}
        >
          <Flex
            direction="column"
            w="100%"
            background="transparent"
            mt={{ base: "50px", md: "150px", lg: "50px", xl: "50px" }}
            mb={{ base: "60px", lg: "20px" }}
            alignItems="center"
          >
            <Box alignItems="left" w="100%">
              <img
                src={bmLogo}
                alt={t('bmLogo2')}
                width="200px"
                style={{ marginBottom: "20px" }}
              />
              <Heading color={titleColor} fontSize="32px" mb="10px">
                {t("signUp")}
              </Heading>
              <Text
                mb="36px"
                ms="4px"
                color={textColor}
                fontWeight="bold"
                fontSize="14px"
              >
                {t("enterYourInfoToRegister")}
              </Text>
            </Box>

            <form onSubmit={handleSubmit} style={{ width: "100%" }}>
              <FormControl mb="24px">
                <FormLabel ms="4px" fontSize="sm" fontWeight="normal" color="white">
                  {t("email")}
                </FormLabel>
                <GradientBorder
                  w={{ base: "100%", lg: "fit-content" }}
                  borderRadius="20px"
                >
                  <Input
                    color="white"
                    bg="rgb(19,21,54)"
                    border="transparent"
                    borderRadius="20px"
                    fontSize="sm"
                    size="lg"
                    w={{ base: "100%", md: "346px" }}
                    maxW="100%"
                    h="46px"
                    type="email"
                    placeholder={t("emailPlaceholder")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </GradientBorder>
              </FormControl>

              <FormControl mb="24px">
                <FormLabel ms="4px" fontSize="sm" fontWeight="normal" color="white">
                  {t("password")}
                </FormLabel>
                <GradientBorder
                  w={{ base: "100%", lg: "fit-content" }}
                  borderRadius="20px"
                >
                  <InputGroup size="md">
                    <Input
                      color="white"
                      bg="rgb(19,21,54)"
                      border="transparent"
                      borderRadius="20px"
                      fontSize="sm"
                      size="lg"
                      w={{ base: "100%", md: "346px" }}
                      maxW="100%"
                      h="46px"
                      type={showPassword ? "text" : "password"}
                      placeholder={t("passwordPlaceholder")}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <InputRightElement width="4.5rem" h="100%">
                      <Button
                        h="1.75rem"
                        size="sm"
                        onClick={() => setShowPassword(!showPassword)}
                        bg="transparent"
                        _hover={{ bg: "transparent" }}
                        color="gray.400"
                      >
                        {showPassword ? t("hide") : t("show")}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                </GradientBorder>
              </FormControl>

              <FormControl mb="24px">
                <FormLabel ms="4px" fontSize="sm" fontWeight="normal" color="white">
                  {t("confirmPassword")}
                </FormLabel>
                <GradientBorder
                  w={{ base: "100%", lg: "fit-content" }}
                  borderRadius="20px"
                >
                  <InputGroup size="md">
                    <Input
                      color="white"
                      bg="rgb(19,21,54)"
                      border="transparent"
                      borderRadius="20px"
                      fontSize="sm"
                      size="lg"
                      w={{ base: "100%", md: "346px" }}
                      maxW="100%"
                      h="46px"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder={t("confirmPasswordPlaceholder")}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                    <InputRightElement width="4.5rem" h="100%">
                      <Button
                        h="1.75rem"
                        size="sm"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        bg="transparent"
                        _hover={{ bg: "transparent" }}
                        color="gray.400"
                      >
                        {showConfirmPassword ? t("hide") : t("show")}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                </GradientBorder>
              </FormControl>

              <FormControl mb="24px">
                <FormLabel ms="4px" fontSize="sm" fontWeight="normal" color="white">
                  {t("companyName")}
                </FormLabel>
                <GradientBorder
                  w={{ base: "100%", lg: "fit-content" }}
                  borderRadius="20px"
                >
                  <Input
                    color="white"
                    bg="rgb(19,21,54)"
                    border="transparent"
                    borderRadius="20px"
                    fontSize="sm"
                    size="lg"
                    w={{ base: "100%", md: "346px" }}
                    maxW="100%"
                    h="46px"
                    placeholder={t("companyNamePlaceholder")}
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                  />
                </GradientBorder>
              </FormControl>

              <FormControl mb="24px">
                <FormLabel ms="4px" fontSize="sm" fontWeight="normal" color="white">
                  {t("howDidYouHear")}
                </FormLabel>
                <GradientBorder
                   w={{ base: "100%", lg: "fit-content" }}
                   borderRadius="20px"
                >
                  <Select
                    color="white"
                    bg="rgb(19,21,54)"
                    border="transparent"
                    borderRadius="20px"
                    fontSize="sm"
                    size="lg"
                    w={{ base: "100%", md: "346px" }}
                    maxW="100%"
                    h="46px"
                    placeholder={t("selectOption")}
                    value={howDidYouHear}
                    onChange={(e) => setHowDidYouHear(e.target.value)}
                    sx={{
                      "> option": {
                        background: "rgb(19,21,54)",
                        color: "white",
                      },
                    }}
                  >
                    <option value="search">{t("searchEngine")}</option>
                    <option value="social">{t("socialMedia")}</option>
                    <option value="friend">{t("friendRecommendation")}</option>
                    <option value="other">{t("other")}</option>
                  </Select>
                </GradientBorder>
              </FormControl>

              {howDidYouHear === "other" && (
                <FormControl mb="24px">
                  <FormLabel ms="4px" fontSize="sm" fontWeight="normal" color="white">
                    {t("pleaseSpecify")}
                  </FormLabel>
                  <GradientBorder
                    w={{ base: "100%", lg: "fit-content" }}
                    borderRadius="20px"
                  >
                    <Input
                      color="white"
                      bg="rgb(19,21,54)"
                      border="transparent"
                      borderRadius="20px"
                      fontSize="sm"
                      size="lg"
                      w={{ base: "100%", md: "346px" }}
                      maxW="100%"
                      h="46px"
                      placeholder={t("otherSourcePlaceholder")}
                      value={otherSource}
                      onChange={(e) => setOtherSource(e.target.value)}
                    />
                  </GradientBorder>
                </FormControl>
              )}

              <Button
                type="submit"
                variant="brand"
                fontSize="15px"
                w="100%"
                maxW="350px"
                h="45"
                mb="24px"
                color="white"
                bg="teal.300"
                _hover={{ bg: "teal.200" }}
                _active={{ bg: "teal.400" }}
              >
                {t("signUp")}
              </Button>
            </form>

            <Flex
              flexDirection="column"
              justifyContent="center"
              alignItems="start"
              maxW="100%"
              mt="0px"
            >
              <Text color={textColor} fontWeight="medium">
                {t("alreadyHaveAccount")}{" "}
                <Link
                  color={titleColor}
                  as="span"
                  ms="5px"
                  fontWeight="bold"
                  onClick={() => navigate("/login")}
                >
                  {t("signIn")}
                </Link>
              </Text>
            </Flex>
          </Flex>

          <Box
            w="100%"
            mb="40px"
            mx="auto"
          >
            <Footer />
          </Box>
        </Flex>

        <Box
          display={{ base: "none", lg: "block" }}
          overflowX="hidden"
          h="100%"
          maxW={{ md: "50vw", lg: "50vw" }}
          minH="100vh"
          w="960px"
          position="absolute"
          left="0px"
        >
          <Box
            bgImage={signInImage}
            w="100%"
            h="100%"
            bgSize="cover"
            bgPosition="50%"
            position="absolute"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
          >
            <Text
              textAlign="center"
              color="white"
              letterSpacing="8px"
              fontSize="20px"
              fontWeight="500"
            >
              {t("levelUp")}
            </Text>
            <Text
              textAlign="center"
              color="transparent"
              letterSpacing="8px"
              fontSize="36px"
              fontWeight="bold"
              bgClip="text !important"
              bg="linear-gradient(94.56deg, #FFFFFF 79.99%, #21242F 102.65%)"
            >
              {t("withBulletMonkey")}
            </Text>
          </Box>
        </Box>
      </Flex>
    </Flex>
  );
};

export default Register;
